import React, { useState } from 'react';
import { FileText, File, Download, Eye, AlertCircle, Loader2 } from 'lucide-react';
import { useAttachmentPreview } from '../../hooks/useAttachmentPreview';
import AttachmentPreviewModal from './AttachmentPreviewModal';

const AttachmentItem = ({ attachment, messageId, isSent }) => {
    const { url, error, loading } = useAttachmentPreview(attachment.storagePath);
    const [showPreview, setShowPreview] = useState(false);

    const getFileIcon = (mimeType) => {
        if (mimeType?.startsWith('image/')) return null; // Will show thumbnail
        if (mimeType === 'application/pdf') return <FileText className="w-4 h-4" />;
        return <File className="w-4 h-4" />;
    };

    const getFileType = (mimeType) => {
        if (mimeType?.startsWith('image/')) return 'Image';
        if (mimeType === 'application/pdf') return 'PDF';
        if (mimeType?.startsWith('text/')) return 'Text';
        return 'File';
    };

    const canPreview = (mimeType) => {
        return mimeType?.startsWith('image/') ||
            mimeType === 'application/pdf' ||
            mimeType?.startsWith('text/');
    };

    const handlePreview = () => {
        if (canPreview(attachment.mimeType) && url) {
            setShowPreview(true);
        }
    };

    const handleDownload = () => {
        if (url) {
            const link = document.createElement('a');
            link.href = url;
            link.download = attachment.filename;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    };

    return (
        <>
            <div
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${isSent
                    ? 'bg-stone-100 border-stone-200 hover:bg-stone-200'
                    : 'bg-white border-notion-border hover:bg-stone-50'
                    } ${loading ? 'opacity-70' : ''}`}
                onClick={canPreview(attachment.mimeType) ? handlePreview : handleDownload}
            >
                {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-stone-500" />
                ) : error ? (
                    <AlertCircle className="w-4 h-4 text-red-500" />
                ) : attachment.mimeType?.startsWith('image/') && url ? (
                    <img
                        src={url}
                        alt={attachment.filename}
                        className="w-8 h-8 object-cover rounded"
                        onError={(e) => {
                            // Hide the broken image
                            e.target.style.display = 'none';
                        }}
                    />
                ) : (
                    getFileIcon(attachment.mimeType)
                )}

                <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium truncate max-w-32" title={attachment.filename}>
                        {attachment.filename}
                    </span>
                    <span className="text-xs text-stone-500">
                        {getFileType(attachment.mimeType)} • {formatFileSize(attachment.size)}
                    </span>
                </div>

                {canPreview(attachment.mimeType) && url && !loading && (
                    <Eye className="w-4 h-4 text-stone-500 hover:text-stone-700" />
                )}

                {!canPreview(attachment.mimeType) && url && !loading && (
                    <Download className="w-4 h-4 text-stone-500 hover:text-stone-700" />
                )}
            </div>

            {/* Preview Modal */}
            {showPreview && url && (
                <AttachmentPreviewModal
                    attachment={attachment}
                    previewUrl={url}
                    onClose={() => setShowPreview(false)}
                />
            )}
        </>
    );
};

export default AttachmentItem;